import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Notification from "../../../components/Notification";
import { apiCall } from "../../../utils/apiCall";

// The types offered when logging an issue. Admins add, rename, deactivate or delete them.
function IssueTypes() {
    const [types, setTypes] = useState([]);
    const [name, setName] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [notice, setNotice] = useState(null);
    // Deactivating and deleting are confirmed first: { type, action: "off" | "delete" }
    const [pending, setPending] = useState(null);
    const [working, setWorking] = useState(false);

    const load = useCallback(async () => {
        const { ok, data } = await apiCall("/issues/types");
        if (ok) setTypes(data);
        else setNotice({ type: "danger", message: data?.message || "The issue types could not be loaded." });
    }, []);

    useEffect(() => {
        document.title = "Issue Types - CFCM Portal";
        load();
    }, [load]);

    const say = (ok, data, success) => setNotice(ok
        ? { type: "success", message: success }
        : { type: "danger", message: data?.name?.[0] || data?.message || "That did not work. Please try again." });

    const add = async (event) => {
        event.preventDefault();
        const { ok, data } = await apiCall("/issues/types", { method: "POST", body: { name } });
        say(ok, data, `"${name.trim()}" added.`);
        if (ok) { setName(""); load(); }
    };

    const rename = async (type) => {
        const { ok, data } = await apiCall(`/issues/types/${type.id}`, { method: "PUT", body: { name: editName } });
        say(ok, data, "Renamed.");
        if (ok) { setEditingId(null); load(); }
    };

    const toggle = async (type) => {
        const { ok, data } = await apiCall(`/issues/types/${type.id}`, { method: "PUT", body: { is_active: !type.is_active } });
        say(ok, data, type.is_active ? `"${type.name}" deactivated. It is no longer offered when logging an issue.` : `"${type.name}" activated. It is offered again.`);
        if (ok) load();
    };

    const remove = async (type) => {
        const { ok, data } = await apiCall(`/issues/types/${type.id}`, { method: "DELETE" });
        say(ok, data, `"${type.name}" deleted.`);
        if (ok) load();
    };

    // Deactivating a type never deletes or changes the issues that use it.
    const usedBy = (count) => (!count
        ? "No issue uses it."
        : `The ${count} issue${count === 1 ? "" : "s"} of this type ${count === 1 ? "is" : "are"} kept as ${count === 1 ? "it is" : "they are"}, nothing is deleted.`);

    const confirmPending = async () => {
        setWorking(true);
        try {
            if (pending.action === "delete") await remove(pending.type);
            else await toggle(pending.type);
        } finally {
            setWorking(false);
            setPending(null);
        }
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <div>
                            <p className="title is-4">Issue Types</p>
                            <p className="cf-muted">Active types are offered when logging an issue. "Other" is always offered, with a box to type the type in.</p>
                        </div>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/issues" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to the Issue Log
                        </Link>
                    </p>
                </div>
            </nav>

            {notice && <Notification type={notice.type} message={notice.message} onClose={() => setNotice(null)} />}

            <form className="box" onSubmit={add}>
                <label className="label" htmlFor="type-name">Add a type</label>
                <div className="field has-addons">
                    <div className="control is-expanded">
                        <input id="type-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Insurance renewal" maxLength={100} required />
                    </div>
                    <div className="control">
                        <button type="submit" className="button primary-bg">Add</button>
                    </div>
                </div>
            </form>

            <div className="table-container">
                <table className="table is-striped is-fullwidth">
                    <thead>
                        <tr><th>Type</th><th>Issues</th><th>Status</th><th></th></tr>
                    </thead>
                    <tbody>
                        {types.map((type) => (
                            <tr key={type.id}>
                                <td>
                                    {editingId === type.id ? (
                                        <div className="field has-addons mb-0">
                                            <div className="control is-expanded"><input className="input is-small" value={editName} onChange={(e) => setEditName(e.target.value)} aria-label="New name" /></div>
                                            <div className="control"><button type="button" className="button is-small primary-bg" onClick={() => rename(type)}>Save</button></div>
                                            <div className="control"><button type="button" className="button is-small" onClick={() => setEditingId(null)}>Cancel</button></div>
                                        </div>
                                    ) : type.name}
                                </td>
                                <td>{type.issues_count}</td>
                                <td>{type.is_active ? <span className="cf-badge is-ok">Active</span> : <span className="cf-badge is-bad">Inactive</span>}</td>
                                <td className="has-text-right cf-nowrap cf-row-actions">
                                    {editingId !== type.id && (
                                        <>
                                            <button type="button" className="button is-small" onClick={() => { setEditingId(type.id); setEditName(type.name); }}>Rename</button>
                                            <button type="button" className="button is-small" onClick={() => (type.is_active ? setPending({ type, action: "off" }) : toggle(type))}>{type.is_active ? "Deactivate" : "Activate"}</button>
                                            {type.issues_count === 0 && <button type="button" className="button is-small is-danger is-light" onClick={() => setPending({ type, action: "delete" })}>Delete</button>}
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmDialog
                isOpen={Boolean(pending)}
                theme={pending?.action === "delete" ? "is-danger" : "is-warning"}
                title={pending?.action === "delete" ? "Delete Issue Type" : "Deactivate Issue Type"}
                message={pending ? `Are you sure you want to ${pending.action === "delete" ? "delete" : "deactivate"} "${pending.type.name}"?` : ""}
                note={pending?.action === "delete"
                    ? "When deleted, it is removed for good. Only types that no issue uses can be deleted."
                    : `When deactivated, it is no longer offered when logging an issue. ${usedBy(pending?.type.issues_count)} You can activate it again at any time.`}
                onConfirm={confirmPending}
                onCancel={() => setPending(null)}
                isLoading={working}
            />
        </main>
    );
}

export default IssueTypes;
